import { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Theme, Typography, useMediaQuery } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { IsEmail, IsNotEmpty } from 'class-validator';
import { useForm } from 'react-hook-form';
import { EventTabsEnum } from '../EventPage/event-page.component';
import { isClient, queryKeys } from '../../../constants';
import { eventClassesService, eventsService, paymentService } from '../../../services';
import { EventUserRolesEnum, TicketWithoutPaymentDetailsDto } from '@sweatpals/shared';
import { Button, Colors, EventBackground, Showable, TextField } from '@sweatpals/react-ui-kit';
import useLink from '../../../utils/hooks/useLink';
import { getPublicFileUrl } from 'utils/file.utils';
import Logo from 'components/ui-v2/logo/logo.component';
import { classValidatorResolver } from '@hookform/resolvers/class-validator';
import { EventTicketCard } from '../../ui-v3/order/event-ticket-card.component';
import ExternalLink from 'components/links/External-link';
import EventActionModal from 'components/ui-v2/event-action-modal/event-action-modal.component';

export class EventTicketsLoginForm {
	@IsNotEmpty({ message: 'Email is required' })
	@IsEmail(undefined, { message: 'Invalid email' })
	email: string;
}

const resolver = classValidatorResolver(EventTicketsLoginForm);

export interface EventTicketsPageProps {
	eventAlias: string;
	shortLocalInstance: string;
	defaultEventTab?: EventTabsEnum;
}

export const EventTicketsPage: FC<EventTicketsPageProps> = props => {
	const { eventAlias, shortLocalInstance } = props;

	const { appLinkByOs } = useLink();
	const [showContactHostModal, setShowContactHostModal] = useState(false);
	const [isLoading, setIsLoading] = useState(false);
	const [tickets, setTickets] = useState<TicketWithoutPaymentDetailsDto[]>([]);

	const openContactHostModal = useCallback(() => {
		setShowContactHostModal(true);
	}, []);

	const closeContactHostModal = useCallback(() => {
		setShowContactHostModal(false);
	}, []);

	const { data: eventData } = useQuery(queryKeys.eventInstance(eventAlias, shortLocalInstance), async () =>
		eventsService.getPublicEventInstanceByAlias(eventAlias, shortLocalInstance, 'event-tickets-page.component')
	);

	const { data: eventClasses } = useQuery(queryKeys.eventClasses(eventData?.baseId, shortLocalInstance), async () =>
		eventData?.baseId && shortLocalInstance
			? eventClassesService.getEventClasses(eventData.baseId, shortLocalInstance)
			: []
	);

	const form = useForm<EventTicketsLoginForm>({
		defaultValues: {
			email: '',
		},
		resolver,
	});

	const handleLoginSubmit = form.handleSubmit(async data => {
		if (!eventData || !data.email) {
			return;
		}

		try {
			setIsLoading(true);

			const tickets = await paymentService.getPublicTicketsByEmail({
				email: data.email,
				eventId: eventData.baseId,
				eventInstanceDate: eventData.instance,
			});

			if (tickets.length === 0) {
				form.setError('email', {
					type: 'tickets-not-found',
					message: 'No ticket found associated with this email.',
				});
				return;
			}

			setTickets(tickets);
		} catch (error) {
			console.error(error);

			form.setError('email', {
				type: 'tickets-not-found',
				message: 'No ticket found associated with this email.',
			});
		} finally {
			setIsLoading(false);
		}
	});

	useEffect(() => {
		if (!isClient) return;

		form.setValue('email', new URLSearchParams(window.location.search).get('email') || '');
	}, []);

	const mdMedia = useMediaQuery((theme: Theme) => theme.breakpoints.up('md'));

	return (
		<Box
			sx={{
				backgroundColor: Colors['1A1A1A'],
				position: 'relative',
				minHeight: '100vh',
			}}
		>
			<EventBackground background={eventData?.avatarId ? getPublicFileUrl(eventData.avatarId) : undefined} />

			<Box
				sx={{
					position: 'relative',
					zIndex: 2,
				}}
			>
				<Box
					sx={{
						display: 'flex',
						alignItems: 'center',
						justifyContent: { xs: 'center', md: 'space-between' },
						px: '60px',
						pt: '30px',
					}}
				>
					<Logo
						type='light'
						sx={{
							maxWidth: '115px',
						}}
					/>
				</Box>

				<Box
					sx={{
						mt: { xs: '30px', md: '145px' },
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
					}}
				>
					<Showable show={!tickets.length}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '24px',
							}}
						>
							<Box
								sx={{
									display: 'flex',
									flexDirection: 'column',
									gap: '10px',
									textAlign: 'center',
								}}
							>
								<Typography
									sx={{
										fontFamily: 'Integral CF, sans-serif',
										fontSize: '24px',
										fontWeight: 600,
										color: Colors.white,
									}}
								>
									Enter Your Email to Access Your Ticket
								</Typography>
								<Typography
									sx={{
										fontFamily: 'Montserrat',
										fontSize: '12px',
										fontWeight: 600,
										color: Colors.white,
									}}
								>
									Enter the email linked to your ticket so we can locate it and help you manage your ticket and sign up
									for classes.
								</Typography>
							</Box>

							<Box
								sx={{
									alignSelf: 'center',
									width: '100%',
									maxWidth: '550px',
									display: 'flex',
									flexDirection: 'column',
									borderRadius: '8px',
									backgroundColor: Colors.black_a50,
									gap: '21px',
									padding: '21px',
								}}
								component='form'
								onSubmit={handleLoginSubmit}
							>
								<TextField
									{...form.register('email')}
									value={form.watch('email')}
									onChange={e => form.setValue('email', e.target.value)}
									onBlur={() => form.trigger('email')}
									error={!!form.formState.errors.email}
									helperText={form.formState.errors.email?.message}
									fullWidth
									label='Email'
									requiredMark
									placeholder='Enter email associated with your ticket'
								/>

								<Button strictColor disabled={isLoading} loading={isLoading} type='submit'>
									Find my ticket
								</Button>
							</Box>
						</Box>
					</Showable>

					<Showable show={tickets.length}>
						<Box
							sx={{
								display: 'flex',
								flexDirection: 'column',
								gap: '21px',
								backgroundColor: Colors.gray_191919_a50,
								border: `1px solid ${Colors.black_a50}`,
								borderRadius: '8px',
								padding: '21px',
								boxSizing: 'border-box',
								maxWidth: '940px',
							}}
						>
							{tickets.map(ticket => (
								<EventTicketCard
									key={ticket.id}
									eventData={eventData}
									ticket={ticket}
									order={ticket.order}
									host={eventData?.author}
									coHost={eventData?.coHost}
									eventClasses={eventClasses}
									/* allow to re-transfer ticket on this page */
									isTicketTransferred={false}
									onTicketTransferButtonClick={
										ticket.order
											? ticketId => {
													window.open(`/order/${ticket.order.id}/transfer-ticket?ticketId=${ticketId}`, '_blank');
											  }
											: undefined
									}
								/>
							))}

							<Showable show={mdMedia}>
								<Box sx={{ display: 'flex', gap: '16px', alignItems: 'center' }}>
									<Typography
										sx={{
											fontFamily: 'Montserrat',
											fontSize: '14px',
											fontWeight: 500,
											lineHeight: '17px',
											color: '#FFFFFF',
											flex: 1,

											'& a': {
												color: '#FFFFFF',
												textDecoration: 'underline',
											},
										}}
									>
										<ExternalLink href={appLinkByOs}>Download Sweatpals app</ExternalLink> to get the latest event
										announcements, meet other attendees and more.
									</Typography>
									<Button
										strictColor
										sx={{
											width: '154px',
											height: '46px',
											padding: '16px',
											borderRadius: '100px',
											fontFamily: 'Integral CF',
											fontSize: '12px',
											fontWeight: 500,
											lineHeight: '14px',
											textAlign: 'center',
											border: '1px solid #FFFFFF',
											backgroundColor: 'transparent',
											color: '#FFFFFF',

											'&:hover': {
												color: '#000000',
												backgroundColor: '#FFFFFF',
											},
										}}
										onClick={openContactHostModal}
									>
										Contact Host
									</Button>
								</Box>
							</Showable>
						</Box>
					</Showable>
				</Box>
			</Box>

			<EventActionModal
				open={showContactHostModal}
				onClose={closeContactHostModal}
				hostId={eventData?.authorId}
				type='host'
				text={
					<>
						Message the host on <br /> the SweatPals app
					</>
				}
			/>
		</Box>
	);
};

export default EventTicketsPage;
